import { animate, AnimatePresence, useMotionValue, useReducedMotion } from 'motion/react';
import React from 'react';
import ReactDOM from 'react-dom';

import { Icon } from '@gitbook/spine-icons';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { type OrganizationAgentLocation } from '../Agent';
import { AgentDockButton } from './AgentDockButton';
import type { AgentDockConversationControls } from './AgentDockConversation';
import { AgentDockTabs, type AgentDockTabDescriptor } from './AgentDockTabs';
import { AGENT_DOCK_WINDOW_WIDTH, AgentDockWindow } from './AgentDockWindow';
import { type AgentConversationHistory, AgentHistoryMenu } from './AgentHistoryMenu';
import { useReportAgentDockBounds } from './layout';
import { AGENT_DOCK_DRAFT_TAB_ID, type AgentDockTab } from './state';

/**
 * How far the window's right edge reaches past the right edge of the tab it is aligned with.
 */
const WINDOW_ANCHOR_OVERHANG = 10;

/**
 * The dock at the bottom of the content column: a row of tabs, and the conversation in front of
 * them in a window of its own.
 *
 * The window is aligned with whichever control the conversation belongs to, which is what the
 * measuring below is for. That is the whole of what this surface adds over the anchored panel, and
 * the reason the two are separate components at all.
 */
export function AgentDockBar(props: {
    /** Where in the layout to render, and what the window is aligned inside of. */
    slot: HTMLElement;
    organizationId: string;
    /** Where the user is, reported to the agent over the window's connection. */
    location: OrganizationAgentLocation;
    tabs: AgentDockTabDescriptor[];
    activeTabId: string | null;
    isDraftActive: boolean;
    minimized: boolean;
    /** The conversation in front, or null when the dock is minimized or holds nothing. */
    conversation: ({ tab: AgentDockTab; title: string } & AgentDockConversationControls) | null;
    /** The other conversations, offered from the button beside "Agent". */
    history: AgentConversationHistory;
    onPressAgent: () => void;
    onSelectTab: (tabId: string) => void;
    onCloseTab: (tabId: string) => void;
    onMinimize: () => void;
}) {
    const {
        slot,
        organizationId,
        location,
        tabs,
        activeTabId,
        isDraftActive,
        minimized,
        conversation,
        history,
        onPressAgent,
        onSelectTab,
        onCloseTab,
        onMinimize,
    } = props;

    // The controls the window can align with, by the active-tab id they represent — the Agent
    // button stands in for the draft. Held in a ref: elements registering must not re-render the
    // dock, and the measuring effect below reads them at its own pace.
    const anchorsRef = React.useRef(new Map<string, HTMLElement>());
    const registerAnchor = useEventCallback((anchorId: string, element: HTMLElement | null) => {
        if (element) {
            anchorsRef.current.set(anchorId, element);
        } else {
            anchorsRef.current.delete(anchorId);
        }
    });
    const registerAgentButtonAnchor = useEventCallback((element: HTMLElement | null) =>
        registerAnchor(AGENT_DOCK_DRAFT_TAB_ID, element)
    );

    // Where the window's right edge goes: the distance from the dock's right edge to the active
    // control's, as a motion value the window translates by. Driven imperatively rather than
    // through state: the same measurement must sometimes move the window smoothly (another tab
    // activated while it is open) and sometimes place it instantly (opening, or the layout
    // resizing under it), and only this effect can tell which is which.
    const desiredAnchorId = isDraftActive ? AGENT_DOCK_DRAFT_TAB_ID : activeTabId;
    const isWindowOpen = conversation !== null;
    const windowX = useMotionValue(-8);
    const windowRoom = useAgentDockWindowRoom(slot);
    const prefersReducedMotion = useReducedMotion();
    const reportBounds = useReportAgentDockBounds();
    // Whether the window was already open at the previous measurement, which is what separates a
    // move the user should see from a position it should simply appear at.
    const wasWindowOpenRef = React.useRef(false);
    // The anchor the window last aligned with. Closing the active tab leaves nothing active while
    // the window is still playing its exit: holding on to the vanished tab's id keeps the
    // measurement empty-handed, so the fading window stays where the tab was instead of snapping
    // away the moment it disappears.
    const lastAnchorIdRef = React.useRef<string>(AGENT_DOCK_DRAFT_TAB_ID);
    React.useLayoutEffect(() => {
        const anchorId = desiredAnchorId ?? lastAnchorIdRef.current;
        lastAnchorIdRef.current = anchorId;

        const measure = () => {
            const anchor = anchorsRef.current.get(anchorId);
            if (!anchor) {
                return null;
            }
            const slotRect = slot.getBoundingClientRect();
            const anchorRect = anchor.getBoundingClientRect();
            // Overhanging the control a little rather than flush with it, so its right edge sits
            // inside the window's corner radius instead of exactly on it. Clamped so a tab near
            // the left edge cannot push the window out of the dock; the margins mirror the bar's
            // own `px-2`.
            const offset = Math.max(
                8,
                Math.min(
                    slotRect.right - anchorRect.right - WINDOW_ANCHOR_OVERHANG,
                    slotRect.width - AGENT_DOCK_WINDOW_WIDTH - 8
                )
            );
            return -offset;
        };

        let target = measure();
        if (target !== null && target !== windowX.get()) {
            if (isWindowOpen && wasWindowOpenRef.current && !prefersReducedMotion) {
                animate(windowX, target, { type: 'spring', duration: 0.3, bounce: 0.15 });
            } else {
                windowX.jump(target);
            }
        }
        wasWindowOpenRef.current = isWindowOpen;

        // Where the window is, for what has to keep clear of it. Its destination rather than
        // the sliding value, so a slide moves the toasts once; and withdrawn as soon as it
        // closes, without waiting for it to fade.
        const report = () => {
            if (!isWindowOpen) {
                reportBounds(null);
                return;
            }
            const right = slot.getBoundingClientRect().right + (target ?? windowX.get());
            reportBounds({ left: right - AGENT_DOCK_WINDOW_WIDTH, right });
        };
        report();

        // Re-measure when the dock or the anchored control resizes — a snap, not a slide: the
        // window is not moving to another tab, the layout is moving under it. Observing also
        // fires once immediately, so the guard compares against the slide's destination rather
        // than the mid-flight value — or that first delivery would cut the slide short.
        const observer = new ResizeObserver(() => {
            const resized = measure();
            if (resized !== null && resized !== (target ?? windowX.get())) {
                target = resized;
                windowX.jump(resized);
            }
            report();
        });
        observer.observe(slot);
        const anchor = anchorsRef.current.get(anchorId);
        if (anchor) {
            observer.observe(anchor);
        }
        return () => observer.disconnect();
    }, [slot, desiredAnchorId, isWindowOpen, tabs, windowX, prefersReducedMotion, reportBounds]);

    return ReactDOM.createPortal(
        <>
            {/* One stable element whatever the conversation, so switching tabs slides the window
                to its new tab instead of remounting it, and closing it can play an exit. */}
            <AnimatePresence>
                {conversation ? (
                    <AgentDockWindow
                        key="window"
                        {...conversation}
                        location={location}
                        x={windowX}
                        room={windowRoom}
                        onMinimize={onMinimize}
                        onOpenConversation={history.onOpenConversation}
                    />
                ) : null}
            </AnimatePresence>
            {/* One gap throughout, so the last tab sits the same distance from the Agent button as
                the Agent button does from the history one. */}
            <div className="flex items-center gap-0.5 px-2 pb-1.5">
                <div className="flex min-w-0 flex-1 justify-end">
                    <AgentDockTabs
                        tabs={tabs}
                        // Minimized means nothing is in front, so no tab reads as active — the
                        // same rule the Agent button follows for its draft.
                        activeTabId={minimized ? null : activeTabId}
                        organizationId={organizationId}
                        registerAnchor={registerAnchor}
                        onSelect={onSelectTab}
                        onClose={onCloseTab}
                    />
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                    {/* Acts as the draft conversation's tab: it opens it, shows it as active, and
                        toggles it away again. A tab only appears once a conversation is started. */}
                    <AgentDockButton
                        ref={registerAgentButtonAnchor}
                        icon={Icon.AIAgent}
                        label="Agent"
                        labelClassName="font-medium"
                        active={isDraftActive && !minimized}
                        onPress={onPressAgent}
                    >
                        Agent
                    </AgentDockButton>
                    <AgentHistoryMenu history={history} />
                </div>
            </div>
        </>,
        slot
    );
}

/**
 * The space above the dock's slot, up to the top of the column it sits in: what the window has to
 * fit into, since the column clips whatever sticks out of it.
 *
 * Measured rather than taken from the viewport, because the column's top sits below whatever
 * banners the frame is showing and moves with them. Watched for as long as the bar is there, so a
 * window resized under an open conversation shortens it rather than cutting it off.
 */
function useAgentDockWindowRoom(slot: HTMLElement): number {
    const [room, setRoom] = React.useState(Infinity);

    React.useLayoutEffect(() => {
        // The content column, which is also what clips the window.
        const column = slot.parentElement;
        if (!column) {
            return;
        }

        const measure = () => {
            setRoom(slot.getBoundingClientRect().top - column.getBoundingClientRect().top);
        };
        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(column);
        observer.observe(slot);
        return () => observer.disconnect();
    }, [slot]);

    return room;
}
